import { httpRequest } from '@/clients/http'

/** A backoffice list page (`response({ page, length, count, result })`, query_assist). */
export interface ListPage<T> {
  page: number
  length: number
  count: number
  result: T[]
}

type Row = Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any

const list = <T = Row>(url: string, query: Record<string, unknown> = {}) =>
  httpRequest<ListPage<T>>({ method: 'get', url, query: { page: 1, length: 50, ...query } })

/**
 * The branch office's online reads and writes (POS.md §18.1 rule 4): history beyond what the device holds, reports,
 * price books, offers, stock moves, suppliers, staff PIN. Every call is the same `/api/backoffice` endpoint the admin
 * uses, with this branch's id or its terminals' ids as the filter.
 */
export const OfficeModel = {
  /** POS orders of the branch's terminals, newest first. */
  orders: (query: { terminal_id: string; page?: number; search?: string; status?: string }) =>
    list('/api/backoffice/pos-order/list', { ...query, order_by: 'created_at,desc' }),

  /** Per-shift figures from `ShiftReport` (POS.md §15.9 rule 18). */
  shiftReports: (query: { branch_id: string; page?: number }) =>
    list('/api/backoffice/pos-report/shifts', { ...query, order_by: 'opened_at,desc' }),

  cashMoves: (query: { terminal_id: string; page?: number; type?: string }) =>
    list('/api/backoffice/terminal-cash', { ...query, order_by: 'created_at,desc' }),

  profitLoss: (query: { branch_id: string; from_date?: string; to_date?: string }) =>
    httpRequest<{ result: Row[] }>({ method: 'get', url: '/api/backoffice/profit-loss/list', query }),

  profitLossByProduct: (query: { branch_id: string; from_date?: string; to_date?: string }) =>
    httpRequest<{ result: Row[] }>({ method: 'get', url: '/api/backoffice/profit-loss/list-by-product', query }),

  /** One of the seven POS reports (`pos-report/*`). */
  report: (path: string, query: Record<string, unknown>) =>
    httpRequest<{ result: Row[]; count?: number }>({ method: 'get', url: `/api/backoffice/pos-report/${path}`, query }),

  priceBooks: () => list('/api/backoffice/price-book', { length: 200 }),
  createPriceBook: (body: { title: string; description?: string }) =>
    httpRequest<{ result: Row }>({ method: 'post', url: '/api/backoffice/price-book', body }),
  updatePriceBook: (id: string, body: { title?: string; description?: string; status?: string }) =>
    httpRequest<{ result: Row }>({ method: 'put', url: `/api/backoffice/price-book/${id}`, body }),
  deletePriceBook: (id: string) => httpRequest<unknown>({ method: 'delete', url: `/api/backoffice/price-book/${id}` }),

  /** Offers are created online (POS.md §18.1 rule 4); the till gets them in the next pull. */
  createOffer: (body: Row) => httpRequest<{ result: Row }>({ method: 'post', url: '/api/backoffice/offer', body }),

  /** Stock moves are online, direct actions (§18.7 Q3, Q8); `branch_id` names this branch. */
  purchase: (body: Row) => httpRequest<unknown>({ method: 'post', url: '/api/backoffice/stock/purchase', body }),
  adjust: (body: Row) => httpRequest<unknown>({ method: 'post', url: '/api/backoffice/stock/adjust', body }),
  transfer: (body: Row) => httpRequest<unknown>({ method: 'post', url: '/api/backoffice/stock/transfer', body }),
  expiry: (query: { branch_id: string; status?: string; page?: number }) => list('/api/backoffice/stock/expiry', query),

  suppliers: () => list('/api/backoffice/supplier', { length: 200 }),
  createSupplier: (body: Row) => httpRequest<{ result: Row }>({ method: 'post', url: '/api/backoffice/supplier', body }),
  updateSupplier: (id: string, body: Row) => httpRequest<{ result: Row }>({ method: 'put', url: `/api/backoffice/supplier/${id}`, body }),

  /** A new offline PIN for a staff member; the response carries it once. */
  resetPin: (staffId: string) =>
    httpRequest<{ result: Row; offline_pin?: string }>({ method: 'put', url: `/api/backoffice/admin/${staffId}`, body: { reset_offline_pin: true } }),
}
